<template>
    <div class="login-page">
        <!-- 顶部简洁视觉区：慧钢琴 + 线条装饰 -->
        <div class="header">
            <div class="brand">
                <img class="brand-icon" :src="logoIcon" alt="">
                <div class="brand-text">慧钢琴</div>
            </div>
            <p class="header-subtitle">
                记录每一次练琴与课堂反馈，让进步看得见。
            </p>
        </div>

        <!-- 中间卡片表单 -->
        <div class="card">
            <div class="card-header">
                <h1 class="card-title">学生登录</h1>
                <p class="card-desc">
                    请输入学生姓名和预留手机号码
                </p>
            </div>

            <van-form @submit="login" class="login-form">
                <van-field v-model="username" label="学生姓名" placeholder="请输入学生姓名" clearable
                    :rules="[{ required: true, message: '请填写学生姓名' }]" />

                <van-field v-model="mobile" type="tel" label="手机号码" placeholder="请输入手机号码" clearable maxlength="11"
                    :rules="[
                        {
                            required: true,
                            pattern: /^1[3-9]\d{9}$/,
                            message: '请输入正确的手机号码'
                        }
                    ]" />

                <div class="login-extra">
                    <span class="extra-tip">请确认信息与报名登记时填写一致</span>
                </div>

                <van-button round block type="primary" native-type="submit" :loading="loading" loading-text="正在登录..."
                    class="login-btn">
                    进入慧钢琴
                </van-button>
            </van-form>
        </div>

        <div class="footer-tip">
            如无法登录，请联系夏老师协助处理
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import logoIcon from '@/assets/logo.png';

export default {
    name: 'FreshLogin',
    data() {
        return {
            logoIcon,
            username: '',
            mobile: '',
            loading: false
        };
    },
    methods: {
        async login() {
            if (this.loading) return;

            this.loading = true;
            try {
                const data = {
                    username: this.username.trim(),
                    mobile: this.mobile.trim()
                };

                const response = await axios.post('/api/login', data);
                const res = response.data;

                if (res.error) {
                    this.$toast(res.message || '登录失败，请检查填写信息');
                    return;
                }

                // 存储用户信息到 Vuex（保持不变）
                this.$store.dispatch('setUserId', res.data.id);
                this.$store.dispatch('setUsername', res.data.username);
                this.$store.dispatch('setToken', res.data.token);

                // 跳转到首页（保持不变）
                this.$router.push('/main');
            } catch (e) {
                this.$toast('登录失败，请稍后重试');
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style lang="less" scoped>
@bg-page: #f4f5fb;
@bg-card: #ffffff;
@primary: #2550ff;
@primary-soft: #e3e8ff;
@text-main: #111827;
@text-sub: #6b7280;
@border-soft: #e5e7eb;

/* 整体布局 */
.login-page {
    min-height: 100vh;
    padding: 32px 20px 24px;
    box-sizing: border-box;
    background:
        radial-gradient(circle at 0 0, #e6edff 0, transparent 45%),
        radial-gradient(circle at 100% 0, #fef3c7 0, transparent 45%),
        linear-gradient(to bottom, @bg-page 0, #f9fafb 100%);
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

/* 顶部区域 */
.header {
    padding-top: 8px;
    margin-bottom: 24px;
}

.brand {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

/* App 图标本身就是圆角方块（PNG 自带透明圆角），不用再加 border-radius 和底色 */
.brand-icon {
    height: 42px;
    width: auto;
    display: block;
    margin-right: 10px;
}

.brand-text {
    font-size: 22px;
    font-weight: 600;
    color: @text-main;
}

.header-subtitle {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
    color: @text-sub;
}

/* 表单卡片 */
.card {
    background: @bg-card;
    border-radius: 18px;
    padding: 20px 18px 18px;
    box-shadow:
        0 18px 40px rgba(148, 163, 184, 0.3),
        0 0 0 1px rgba(226, 232, 240, 0.7);
    box-sizing: border-box;
}

.card-header {
    margin-bottom: 12px;
}

.card-title {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 600;
    color: @text-main;
}

.card-desc {
    margin: 0;
    font-size: 13px;
    color: @text-sub;
}

/* 表单样式：字体适当加大 */
.login-form {
    margin-top: 6px;

    :deep(.van-cell) {
        padding-left: 0;
        padding-right: 0;
        padding-top: 12px;
        padding-bottom: 12px;
        font-size: 15px;
        background: transparent;

        &::after {
            left: 0;
            right: 0;
            border-bottom-color: @border-soft;
        }
    }

    :deep(.van-field__label) {
        width: 80px;
        color: @text-sub;
        font-size: 14px;
    }

    :deep(.van-field__control) {
        font-size: 15px;
        color: @text-main;
    }

    :deep(.van-field__control::placeholder) {
        color: rgba(156, 163, 175, 0.9);
    }

    :deep(.van-cell__value) {
        color: @text-main;
    }
}

/* 小提示 */
.login-extra {
    display: flex;
    justify-content: flex-start;
    margin-top: 10px;
    margin-bottom: 14px;
}

.extra-tip {
    font-size: 12px;
    color: @text-sub;
}

/* 按钮 */
.login-btn {
    margin-top: 4px;
    height: 46px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 999px;
    border: none;
    background-image: linear-gradient(135deg, @primary, #4f7dff);
    box-shadow:
        0 12px 26px rgba(129, 140, 248, 0.45),
        0 0 0 1px rgba(59, 130, 246, 0.25);
}

/* 底部提示 */
.footer-tip {
    margin-top: auto;
    padding-top: 18px;
    font-size: 12px;
    color: @text-sub;
    text-align: center;
}

/* 小屏适配 */
@media (max-width: 375px) {
    .login-page {
        padding: 24px 16px 18px;
    }

    .card {
        padding: 18px 14px 16px;
    }

    .brand-text {
        font-size: 20px;
    }
}
</style>
